/**
 * mixin-resolver.ts
 *
 * Resolves agent composition mixin refs (persona, ruleset, knowledge) to text
 * for rendering into the agent definition at install time.
 *
 * Three ref forms:
 *   Local path  — "./extra-rules.md" or "/abs/path/rules.md"
 *                 Read directly relative to agentDir (or absolute).
 *   Bare name   — "skaile-base"
 *                 Resolved from repos as `${defaultKind}:skaile-base`
 *   Qualified   — "persona:researcher@ext-repo#v2.0"
 *                 Full AssetRef syntax; repo qualifier searches specific repo only.
 *
 * Knowledge bundles (kind="knowledge") resolve to a directory — all *.md files
 * inside are loaded in priority order (same priority-based loadKnowledge logic used by the asset-manager).
 */

import fs from "node:fs/promises";
import path from "node:path";
import type { AgentManifest, CatalogEntry } from "@skaile/workspaces/core";

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * A resolved mixin with its content loaded from disk.
 *
 * @docLink packages/runner/dev-guide#key-conventions
 */
export interface ResolvedMixin {
  /** Text content ready for system prompt injection. */
  content: string;
  /** Absolute path to the source file, for debugging. */
  source: string;
}

/**
 * Pre-built repo catalog map: repoName → CatalogEntry[].
 * Built by the caller (session-builder) from wsConfig.repositories using
 * ensureRepo() + scanRepo() from @skaile/workspaces/core/repo-manager.
 * Local repos are scanned directly; remote repos use the global cache.
 * @docLink packages/runner/dev-guide#key-conventions
 */
export type RepoMap = Record<string, CatalogEntry[]>;

/**
 * Content of an agent mixin, loaded from `CONVENTIONS.md` or `RULES.md`.
 *
 * @docLink packages/runner/dev-guide#key-conventions
 */
export interface AgentMixinContent {
  personas: ResolvedMixin[];
  rules: ResolvedMixin[];
  knowledge: ResolvedMixin[];
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Resolve a single mixin ref to its text content.
 * Returns null if the ref cannot be resolved (missing file or unknown catalog name).
 * Never throws — callers filter nulls.
 * @docLink packages/runner/dev-guide#key-conventions
 */
export async function resolveMixin(
  ref: string,
  defaultKind: string,
  agentDir: string,
  repos: RepoMap,
): Promise<ResolvedMixin | null> {
  // ── Local path ────────────────────────────────────────────────────────────
  if (ref.startsWith(".") || ref.startsWith("/")) {
    const absPath = ref.startsWith("/") ? ref : path.join(agentDir, ref);
    if (defaultKind === "knowledge") {
      return loadKnowledgeDir(absPath);
    }
    const content = await readFileSafe(absPath);
    if (!content) return null;
    return { content: stripFrontmatter(content).trim(), source: absPath };
  }

  // ── Catalog ref ───────────────────────────────────────────────────────────
  const { kind, name, repoQualifier } = parseRef(ref, defaultKind);
  const entry = findInRepos(repos, kind, name, repoQualifier);
  if (!entry) return null;

  if (entry.kind === "knowledge") {
    // knowledge.yaml source — load the sibling directory
    return loadKnowledgeDir(path.dirname(entry.source));
  }

  // persona, ruleset, skill, contract — read manifest file, strip frontmatter
  const raw = await readFileSafe(entry.source);
  if (!raw) return null;
  return { content: stripFrontmatter(raw).trim(), source: entry.source };
}

/**
 * Resolve all mixin arrays from a manifest.
 * All three kinds resolve concurrently. Unresolvable refs are silently skipped.
 * @docLink packages/runner/dev-guide#key-conventions
 */
export async function resolveAgentMixins(
  manifest: Pick<AgentManifest, "persona" | "rules" | "knowledge">,
  agentDir: string,
  repos: RepoMap,
): Promise<AgentMixinContent> {
  const resolve = (
    refs: string[] | undefined,
    kind: "persona" | "ruleset" | "knowledge",
  ): Promise<(ResolvedMixin | null)[]> =>
    Promise.all((refs ?? []).map((r) => resolveMixin(r, kind, agentDir, repos)));

  const [personaResults, rulesResults, knowledgeResults] = await Promise.all([
    resolve(manifest.persona, "persona"),
    resolve(manifest.rules, "ruleset"),
    resolve(manifest.knowledge, "knowledge"),
  ]);

  return {
    personas: personaResults.filter((r): r is ResolvedMixin => r !== null),
    rules: rulesResults.filter((r): r is ResolvedMixin => r !== null),
    knowledge: knowledgeResults.filter((r): r is ResolvedMixin => r !== null),
  };
}

// ── Internal helpers ──────────────────────────────────────────────────────────

/** Read a file, return empty string on any error. */
async function readFileSafe(filePath: string): Promise<string> {
  try {
    return await fs.readFile(filePath, "utf-8");
  } catch {
    return "";
  }
}

/** Strip YAML frontmatter (--- delimited) from markdown content. */
function stripFrontmatter(text: string): string {
  return text.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, "");
}

/**
 * Parse a mixin ref string into kind, name, and optional repo qualifier.
 * Handles: "skaile-base", "persona:skaile-base", "ruleset:safety@skaile-ai#v2"
 */
function parseRef(
  ref: string,
  defaultKind: string,
): { kind: string; name: string; repoQualifier: string | undefined } {
  let rest = ref.trim();
  let kind = defaultKind;

  // Extract kind: prefix
  const colonIdx = rest.indexOf(":");
  if (colonIdx !== -1) {
    kind = rest.slice(0, colonIdx).trim();
    rest = rest.slice(colonIdx + 1).trim();
  }

  // Extract #pin suffix (drop it — we use whatever's installed)
  const hashIdx = rest.indexOf("#");
  if (hashIdx !== -1) rest = rest.slice(0, hashIdx).trim();

  // Extract @repo qualifier
  let repoQualifier: string | undefined;
  const atIdx = rest.indexOf("@");
  if (atIdx !== -1) {
    repoQualifier = rest.slice(atIdx + 1).trim() || undefined;
    rest = rest.slice(0, atIdx).trim();
  }

  return { kind, name: rest, repoQualifier };
}

/** Find a CatalogEntry by kind + name across all repos (or a specific repo). */
function findInRepos(
  repos: RepoMap,
  kind: string,
  name: string,
  repoQualifier: string | undefined,
): CatalogEntry | undefined {
  const searchRepos = repoQualifier
    ? repos[repoQualifier]
      ? [repos[repoQualifier]]
      : []
    : Object.values(repos);

  for (const entries of searchRepos) {
    const found = entries.find((e) => e.kind === kind && e.name === name);
    if (found) return found;
  }
  return undefined;
}

/**
 * Load all *.md files from a knowledge directory, in priority order.
 * Reads index.yaml (same priority-based loadKnowledge format used by the asset-manager) when present.
 * Returns combined content joined with \n\n---\n\n.
 */
async function loadKnowledgeDir(dir: string): Promise<ResolvedMixin | null> {
  let entries: string[];
  try {
    entries = (await fs.readdir(dir)).filter((e) => e.endsWith(".md"));
  } catch {
    return null;
  }

  // Parse index.yaml for priority ordering
  const priorityMap: Record<string, number> = {};
  const indexContent = await readFileSafe(path.join(dir, "index.yaml"));
  if (indexContent) {
    const pathMatches = [...indexContent.matchAll(/path:\s*(\S+)/g)];
    const priorityMatches = [...indexContent.matchAll(/priority:\s*(\d+)/g)];
    pathMatches.forEach((m, i) => {
      const filename = m[1];
      if (!filename) return;
      const priority = priorityMatches[i] ? parseInt(priorityMatches[i][1] ?? "99", 10) : 99;
      priorityMap[filename] = priority;
    });
  }

  const docs = await Promise.all(
    entries.map(async (filename) => ({
      filename,
      priority: priorityMap[filename] ?? 99,
      content: (await readFileSafe(path.join(dir, filename))).trim(),
    })),
  );

  const sorted = docs
    .filter((d) => d.content)
    .sort((a, b) => a.priority - b.priority || a.filename.localeCompare(b.filename));

  if (sorted.length === 0) return null;

  return {
    content: sorted.map((d) => d.content).join("\n\n---\n\n"),
    source: dir,
  };
}
